// ---------------------------------------------------------------------------
// Shared helpers for all analytics modules
// ---------------------------------------------------------------------------

/**
 * Format a date-range params object into a human-readable label.
 *
 * @param {{ from_date?: string, to_date?: string }} params
 * @returns {string} e.g. "2026-01-01 - 2026-01-31" or "2026-03-19"
 */
export function formatDateRange(params) {
  if (!params || !params.from_date) return ""
  if (params.from_date === params.to_date) return params.from_date
  return `${params.from_date} - ${params.to_date}`
}

// ---------------------------------------------------------------------------
// Comparison helpers
// ---------------------------------------------------------------------------

/**
 * Calculate the percentage change between two numeric values.
 *
 * @param {number} current  Current-period value
 * @param {number} previous Previous-period value
 * @returns {number} Percentage change (e.g. 12.34)
 */
export function calculatePercentageChange(current, previous) {
  if (!previous || previous === 0) return current > 0 ? 100 : 0
  return +(((current - previous) / previous) * 100).toFixed(2)
}

/**
 * Derive a trend direction from a numeric delta.
 *
 * @param {number} delta  current − previous
 * @returns {"up"|"down"|"neutral"}
 */
export function deriveTrend(delta) {
  if (delta > 0) return "up"
  if (delta < 0) return "down"
  return "neutral"
}

/**
 * Given two flat totals objects, produce a metrics object with
 * `{ value, comparisonValue, delta, percentChange, trend }` for every key.
 *
 * By default iterates over all keys in `current`. Pass an explicit `keys`
 * array to limit which keys are compared (useful when the totals contain
 * non-numeric display values like formatted strings).
 *
 * @param {Object} current   Primary-period totals
 * @param {Object} previous  Comparison-period totals
 * @param {string[]} [keys]  Optional subset of keys to compare
 * @returns {Object} Keyed by metric name
 */
export function computeComparison(current, previous, keys) {
  const iterKeys = keys || Object.keys(current)
  const result = {}

  for (const key of iterKeys) {
    const cur = typeof current[key] === "string" ? parseFloat(current[key]) : current[key]
    const prev = typeof previous[key] === "string" ? parseFloat(previous[key]) : previous[key]
    const delta = +(cur - prev).toFixed(2)
    const percentChange = calculatePercentageChange(cur, prev)
    const trend = deriveTrend(delta)

    result[key] = { value: cur, comparisonValue: prev, delta, percentChange, trend }
  }

  return result
}

